import * as React from "react";

import {
    createError,
    getUserFromStorage,
    MessageSend,
    RelodeApiError,
    telemetryService,
    useNotifications,
} from "../../..";
import { convertFileResponse } from "../../../backend/responses";
import { Attachment } from "../../../modules";
import { messageService } from "../messaging.service";
import {
    creators,
    MessageComposerActionTypes,
} from "./messageComposerActionTypes";


export const useMessageComposerActions = (
    dispatch: React.Dispatch<MessageComposerActionTypes>
) => {
    const { actions: notifications } = useNotifications();

    const getUnread = async () => {
        dispatch(creators.getUnread());
        try {
            const user = getUserFromStorage();
            if (!user) return;

            const count = await messageService.getUnreadCount();

            dispatch(creators.getUnreadSuccess(count));
        } catch (err) {
            telemetryService.trackEvent("messages.messageActions.getUnread", {
                message: JSON.stringify(err),
            });
        }
    };

    const clearError = () => dispatch(creators.clearError());
    const clearFileAttachments = () =>
        dispatch(creators.clearFileAttachments());

    const sendMessage = async (message: MessageSend) => {
        dispatch(creators.sendMessage());
        try {
            await messageService.sendMessage(message);
            dispatch(creators.sendMessageSuccess());
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.sendMessageFailure(err));
                    notifications.showArea(
                        "error",
                        err.summary,
                        "messageComposerError"
                    );
                    return;
                }
            }
            throw createError(err, "core.messages.messageActions.sendMessage");
        }
    };

    const uploadAttachment = async (file: File) => {
        try {
            dispatch(creators.attachmentStartUpload());
            const result = await messageService.uploadAttachment(file);

            const attachment = convertFileResponse(result);

            dispatch(creators.attachmentUploadSucceeded(attachment));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.attachmentUploadFailed(err?.summary));
                    return;
                }
            } else if (err instanceof TypeError) {
                dispatch(
                    creators.attachmentUploadFailed(
                        "there was a problem uploading your attachment, please contact support if this continues"
                    )
                );
                return;
            }
            throw createError(
                err,
                "core.messages.messageActions.uploadAttachment"
            );
        }
    };
    const updateAttachments = (attachments: Attachment[]) => {
        dispatch(creators.updateAttachments(attachments));
    };

    const copyAttachments = async (messageId: number) => {
        try {
            dispatch(creators.copyAttachmentsStarted());
            const response = await messageService.copyAttachments(messageId);

            const result = response.map(r => convertFileResponse(r));

            dispatch(creators.copyAttachmentsSucceeded(result));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
            } else if (err instanceof TypeError) {
                dispatch(
                    creators.attachmentUploadFailed(
                        "there was a problem copying your attachment, please contact support if this continues"
                    )
                );
                return;
            }
            throw createError(
                err,
                "core.messages.messageActions.copyAttachments"
            );
        }
    };

    return {
        getUnread,
        sendMessage,
        clearError,
        clearFileAttachments,
        uploadAttachment,
        updateAttachments,
        copyAttachments,
    };
};

export type MessageComposerActions = ReturnType<
    typeof useMessageComposerActions
>;
